import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BRAND } from "@/lib/config";
import { useTiers } from "@/lib/useTiers";
import { useAuth } from "@/lib/auth";
import CheckoutModal from "@/components/CheckoutModal";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Flame, Activity, BookOpen, CheckCircle2, Lock, Mail } from "lucide-react";
import NewsletterSection from "@/components/NewsletterSection";

export default function Landing() {
  const navigate = useNavigate();
  const { tiers } = useTiers();
  const { user } = useAuth();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutTier, setCheckoutTier] = useState(null);

  const startCheckout = (tierKey) => {
    if (!user) {
      // Remember which tier the guest wanted, then push them through signup —
      // Register.jsx redirects to /pricing?checkout=<key> on success.
      try { localStorage.setItem("intended_tier", tierKey); } catch {}
      navigate(`/register?next=pricing&tier=${encodeURIComponent(tierKey)}`);
      return;
    }
    setCheckoutTier(tierKey);
    setCheckoutOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden">
      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-40 glass">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-8 h-16">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-brand">
            <img src={BRAND.logo} alt="Dr Thrive 365" className="h-9 w-9 rounded-md object-cover" />
            <span className="font-display text-xl">Dr Thrive 365</span>
          </Link>
          <div className="hidden sm:flex items-center gap-6 text-sm text-white/70">
            <a href="#book" className="hover:text-white">Book</a>
            <a href="#pathway" className="hover:text-white">Pathway</a>
            <a href="#pillars" className="hover:text-white">Pillars</a>
            <a href="#pricing" className="hover:text-white">Pricing</a>
            <Link to="/contact" className="hover:text-white" data-testid="nav-contact">Contact</Link>
            <Link to="/login" className="hover:text-white" data-testid="nav-signin">Sign in</Link>
          </div>
          <div className="sm:hidden flex items-center gap-4">
            <Link to="/contact" className="text-sm text-white/70" data-testid="mobile-contact">Contact</Link>
            <Link to="/login" className="text-sm text-white/70" data-testid="mobile-signin">Sign in</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-8 overflow-hidden min-h-[90vh] flex items-center">
        {/* Dr. Slava portrait — desktop, right-aligned, anchored to top so face is always visible */}
        <img
          src="https://customer-assets.emergentagent.com/job_wellness-vault-24/artifacts/kpm48hnd_Slava%20Black.png"
          alt=""
          aria-hidden="true"
          data-testid="hero-portrait"
          className="hidden md:block absolute top-0 right-0 h-full w-auto max-w-[60%] object-cover object-top pointer-events-none select-none"
          style={{
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.25) 15%, rgba(0,0,0,0.8) 42%, #000 70%)",
            maskImage:
              "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.25) 15%, rgba(0,0,0,0.8) 42%, #000 70%)",
            filter: "grayscale(15%) contrast(1.05) brightness(0.92)",
            opacity: 0.9,
          }}
        />
        {/* Left vignette — keeps headline crisp */}
        <div
          aria-hidden="true"
          className="hidden md:block absolute inset-y-0 left-0 w-3/5 pointer-events-none"
          style={{
            background:
              "linear-gradient(to right, #050505 38%, rgba(5,5,5,0.7) 60%, rgba(5,5,5,0) 90%)",
          }}
        />
        {/* Mobile portrait — top, soft */}
        <img
          src="https://customer-assets.emergentagent.com/job_wellness-vault-24/artifacts/kpm48hnd_Slava%20Black.png"
          alt=""
          aria-hidden="true"
          className="md:hidden absolute top-0 right-0 w-3/4 h-auto object-contain pointer-events-none select-none"
          style={{
            WebkitMaskImage:
              "linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.12) 45%, transparent 80%)",
            maskImage:
              "linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.12) 45%, transparent 80%)",
            filter: "grayscale(25%) brightness(0.7)",
            opacity: 0.5,
          }}
        />

        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#FF8A00]/20 blur-[140px]" />
          <div className="absolute top-1/4 -right-32 w-[400px] h-[400px] rounded-full bg-[#E52E71]/15 blur-[120px]" />
        </div>
        <div className="max-w-5xl mx-auto relative z-10 w-full">
          <div className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#FF8A00] fade-up">
            <Sparkles size={14} /> Dr. Slava's private health optimization lab
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] mt-6 fade-up delay-1">
            Rewire your biology.<br />
            <span className="text-brand-gradient">Restore</span> your energy.<br />
            Step into purpose.
          </h1>
          <p className="text-lg sm:text-xl text-white/60 max-w-2xl mt-8 leading-relaxed fade-up delay-2">
            A structured transformation ecosystem combining peptide science, hormone optimization,
            neuroplasticity, and mindset — guided daily.
          </p>
          <div className="flex flex-wrap gap-3 mt-10 fade-up delay-3">
            <Button
              onClick={() => navigate("/book-session")}
              data-testid="hero-book-cta"
              className="rounded-full px-8 py-6 text-base bg-gradient-to-r from-[#FF8A00] to-[#E52E71] hover:opacity-90 text-white border-0"
            >
              Book a Session with Dr. Slava <ArrowRight className="ml-2" size={18} />
            </Button>
            <Button
              onClick={() => navigate("/login")}
              data-testid="hero-secondary-cta"
              variant="outline"
              className="rounded-full px-8 py-6 text-base bg-transparent border-white/15 hover:bg-white/5"
            >
              Sign in
            </Button>
          </div>
          <div className="mt-14 grid grid-cols-3 gap-4 max-w-xl fade-up delay-4">
            <Stat k="14" v="core lessons" />
            <Stat k="8" v="daily protocols" />
            <Stat k="4" v="program tiers" />
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section id="pillars" className="px-4 sm:px-8 py-24">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00] mb-3">Four pillars</div>
              <h2 className="font-display text-4xl sm:text-5xl">Where medicine meets meaning.</h2>
            </div>
            <p className="text-white/50 max-w-md">
              Each pillar sharpens the next — this is not a course, it is a sequence of transformations.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { t: "Peptides", d: "Signaling molecules that rebuild tissue, sharpen cognition, accelerate recovery.", i: Activity, c: "#FF8A00" },
              { t: "Hormones", d: "Regulate cortisol, estrogen, testosterone, thyroid, insulin — in rhythm.", i: Flame, c: "#E52E71" },
              { t: "Neuroplasticity", d: "Replace the default story. Install focus, resilience, discipline.", i: BookOpen, c: "#FFB347" },
              { t: "Faith & Mindset", d: "Purpose fuels protocol. Discipline is love in action.", i: Sparkles, c: "#FFD58A" },
            ].map((p, i) => (
              <div key={p.t} className={`glass rounded-2xl p-6 hover:-translate-y-1 transition-transform fade-up delay-${i+1}`}>
                <p.i size={22} style={{ color: p.c }} />
                <h3 className="font-display text-2xl mt-4">{p.t}</h3>
                <p className="text-sm text-white/55 mt-2 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pathway */}
      <section id="pathway" className="px-4 sm:px-8 py-24 bg-[#080808]">
        <div className="max-w-5xl mx-auto">
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00] mb-3">The Pathway</div>
          <h2 className="font-display text-4xl sm:text-5xl mb-14">A staircase, not a leap.</h2>
          <div className="space-y-4">
            {tiers.map((t, i) => (
              <div key={t.tier_key || t.key} className="glass rounded-2xl p-6 flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-4 flex-1 min-w-[260px]">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-display text-xl ${t.popular ? "bg-gradient-to-br from-[#FF8A00] to-[#E52E71]" : "bg-white/10"}`}>
                    {i+1}
                  </div>
                  <div>
                    <div className="font-display text-2xl">{t.name}</div>
                    <div className="text-sm text-white/50">{t.sub}</div>
                  </div>
                </div>
                <div className="font-display text-2xl text-brand-gradient">{t.price}<span className="text-xs text-white/40 ml-2">{t.cadence}</span></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Book a Session with Dr. Slava */}
      <section id="book" className="px-4 sm:px-8 py-24 relative overflow-hidden" data-testid="landing-book-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 left-1/3 w-[500px] h-[500px] rounded-full bg-[#FF8A00]/10 blur-[160px]" />
          <div className="absolute -bottom-40 right-1/4 w-[400px] h-[400px] rounded-full bg-[#E52E71]/10 blur-[140px]" />
        </div>
        <div className="max-w-6xl mx-auto relative z-10 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <div className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#FF8A00] mb-3">
              <Sparkles size={14} /> 1-on-1 with Dr. Slava
            </div>
            <h2 className="font-display text-4xl sm:text-5xl leading-[1.05]">
              A personalized protocol,<br />
              <span className="text-brand-gradient">designed for you.</span>
            </h2>
            <p className="text-white/60 mt-6 leading-relaxed max-w-md">
              A short intake so Dr. Slava can review your goals, medical history and labs
              before you meet. Walk away with a peptide, hormone and nervous-system plan
              built for your body.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-white/70 max-w-md">
              {[
                "Deep-dive review of your health goals & current state",
                "Personalized peptide + hormone protocol design",
                "90-day action plan with clear next steps",
                "Emotional & nervous-system regulation reflection",
              ].map((line, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-[#FF8A00] mt-0.5 shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Button
                onClick={() => navigate("/book-session")}
                data-testid="landing-book-cta"
                className="rounded-full px-8 py-6 text-base bg-gradient-to-r from-[#FF8A00] to-[#E52E71] hover:opacity-90 border-0"
              >
                Book your session <ArrowRight className="ml-2" size={18} />
              </Button>
              <div className="text-[11px] text-white/40 mt-3 tracking-wide">
                No account needed to book · Payment via Stripe · Cancel up to 24h before.
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-white/8 relative">
              <img
                src="https://customer-assets.emergentagent.com/job_wellness-vault-24/artifacts/kpm48hnd_Slava%20Black.png"
                alt="Dr. Slava"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/25 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="font-display text-2xl">Dr. Slava</div>
                <div className="text-sm text-white/60 mt-1">Peptide integration · hormone optimization · nervous system</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="px-4 sm:px-8 py-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00] mb-3">Choose your entry</div>
          <h2 className="font-display text-4xl sm:text-5xl mb-12">Pay once. Or arrive every day.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {tiers.map((t) => (
              <div
                key={t.tier_key || t.key}
                className={`relative rounded-2xl p-6 border flex flex-col ${
                  t.popular
                    ? "border-[#FF8A00]/40 bg-gradient-to-b from-[#FF8A00]/10 to-transparent"
                    : "border-white/8 bg-[#0A0A0A]"
                }`}
              >
                {t.popular && (
                  <div className="absolute -top-3 left-6 text-[10px] tracking-[0.22em] uppercase bg-gradient-to-r from-[#FF8A00] to-[#E52E71] px-3 py-1 rounded-full">
                    Most loved
                  </div>
                )}
                <div className="font-display text-xl mb-1">{t.name}</div>
                <div className="text-xs text-white/50 mb-5">{t.sub}</div>
                <div className="font-display text-4xl">{t.price}<span className="text-xs text-white/40 ml-2">{t.cadence}</span></div>
                <ul className="mt-6 space-y-2 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-white/75">
                      <CheckCircle2 size={14} className="mt-0.5 text-[#FF8A00] shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => startCheckout(t.tier_key || t.key)}
                  data-testid={`pricing-cta-${t.tier_key || t.key}`}
                  className={`mt-6 rounded-full ${
                    t.popular
                      ? "bg-gradient-to-r from-[#FF8A00] to-[#E52E71] hover:opacity-90"
                      : "bg-white/15 border border-white/20 text-white hover:bg-white/25"
                  }`}
                >
                  {t.popular ? "Get started" : `Pay ${t.price} · ${t.cadence}`}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <NewsletterSection />

      {/* Closing CTA */}
      <section className="px-4 sm:px-8 py-24 bg-gradient-to-b from-transparent to-[#080808]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-4xl sm:text-6xl leading-tight">
            Your energy is not a mystery.<br /><span className="text-brand-gradient">It's a protocol.</span>
          </h2>
          <Button
            onClick={() => navigate("/register")}
            data-testid="closing-cta"
            className="mt-10 rounded-full px-10 py-7 text-base bg-gradient-to-r from-[#FF8A00] to-[#E52E71]"
          >
            Begin now <ArrowRight className="ml-2" size={18} />
          </Button>
          <p className="text-[11px] text-white/30 mt-10 max-w-xl mx-auto">
            <Lock size={12} className="inline mr-1" />
            This content is for educational purposes only and not medical advice. Always consult a qualified healthcare provider before starting any protocol.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[12px] text-white/45">
            <Link to="/contact" className="hover:text-white" data-testid="footer-contact">Contact</Link>
            <span className="text-white/15">·</span>
            <a href="mailto:contact@drthrive365.com" className="hover:text-white" data-testid="footer-email">contact@drthrive365.com</a>
            <span className="text-white/15">·</span>
            <a href="#newsletter" className="hover:text-white" data-testid="footer-newsletter">Newsletter</a>
          </div>
        </div>
      </section>
      <CheckoutModal open={checkoutOpen} onOpenChange={setCheckoutOpen} tierKey={checkoutTier} />
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div>
      <div className="font-display text-4xl text-brand-gradient">{k}</div>
      <div className="text-xs text-white/50 tracking-wide uppercase">{v}</div>
    </div>
  );
}
